import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import {
  Save,
  ArrowBack,
  CheckCircleRounded,
  RadioButtonUncheckedRounded,
  GroupAddOutlined,
  GroupsOutlined,
  CloudUploadOutlined,
  Business as BusinessIcon,
  AutoAwesomeOutlined,
} from '@mui/icons-material';
import { useOrganization } from '../context/OrganizationContext';
import { ledeSx, panelShadow } from '../theme/styleHelpers';

interface JourneyStep {
  label: string;
  description: string;
  icon: React.ReactNode;
  active?: boolean;
}

const JourneyRow: React.FC<{ step: JourneyStep; index: number; isLast: boolean }> = ({ step, index, isLast }) => (
  <Box sx={{ display: 'flex', gap: 1.5, position: 'relative' }}>
    {/* Connector line on the left */}
    {!isLast && (
      <Box
        sx={(theme) => ({
          position: 'absolute',
          left: 11,
          top: 28,
          bottom: -12,
          width: '2px',
          background: theme.palette.divider,
        })}
      />
    )}
    {/* Status indicator */}
    <Box
      sx={(theme) => ({
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        zIndex: 1,
        background: step.active
          ? theme.palette.brand.teal
          : theme.palette.background.paper,
        border: step.active ? 'none' : `2px solid ${theme.palette.divider}`,
        color: step.active ? '#fff' : theme.palette.text.secondary,
      })}
    >
      {step.active ? (
        <Typography sx={{ fontSize: 12, fontWeight: 800, lineHeight: 1 }}>{index + 1}</Typography>
      ) : (
        <Typography sx={(theme) => ({ fontSize: 12, fontWeight: 700, lineHeight: 1, color: theme.palette.text.secondary })}>
          {index + 1}
        </Typography>
      )}
    </Box>
    {/* Content */}
    <Box sx={{ flex: 1, pb: isLast ? 0 : 2.5 }}>
      <Typography
        sx={(theme) => ({
          fontSize: 14,
          fontWeight: 700,
          color: step.active ? theme.palette.text.primary : theme.palette.text.secondary,
          letterSpacing: '-0.01em',
        })}
      >
        {step.label}
      </Typography>
      <Typography sx={(theme) => ({ fontSize: 12.5, color: theme.palette.text.secondary, mt: 0.25, lineHeight: 1.45 })}>
        {step.description}
      </Typography>
    </Box>
  </Box>
);

const OrganizationCreate: React.FC = () => {
  const navigate = useNavigate();
  const { createOrganization, refreshData, setCurrentOrganizationId } = useOrganization();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Use the context method to create the organization via the API
      const newOrg = await createOrganization({ name, description });

      // Refresh the data in context to update all UI components
      await refreshData();

      // Set the newly created organization as the active organization
      // and navigate to its detail page (shouldNavigate=true)
      setCurrentOrganizationId(newOrg.id, true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create organization';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const journeySteps: JourneyStep[] = [
    {
      label: 'Create your organization',
      description: 'Set the workspace name and a short description. You become the first OrgAdmin.',
      icon: <BusinessIcon />,
      active: true,
    },
    {
      label: 'Create your first team',
      description: 'Teams live inside an organization. Each team has its own content library and members.',
      icon: <GroupsOutlined />,
    },
    {
      label: 'Invite teammates',
      description: 'Add OrgAdmins, TeamAdmins, or TeamMembers. Roles control what they can see and do.',
      icon: <GroupAddOutlined />,
    },
    {
      label: 'Upload content',
      description: 'Drop in PDFs, slides, video, audio, or images. Search, tag, and share from there.',
      icon: <CloudUploadOutlined />,
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6, ml: 0, mx: 0 }}>
      <Typography
        variant="h1"
        sx={{
          fontSize: 32,
          fontWeight: 800,
          letterSpacing: '-0.025em',
          lineHeight: 1.15,
          marginBottom: 1,
        }}
      >
        Create a new organization
      </Typography>

      <Typography sx={ledeSx}>
        Set up a new workspace for your team. You'll be the first OrgAdmin.
      </Typography>

      <Grid container spacing={3}>
        {/* Form column */}
        <Grid item xs={12} md={7}>
          <Box
            sx={(theme) => ({
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '14px',
              padding: 3,
              boxShadow: panelShadow(theme),
            })}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    label="Organization Name"
                    value={name}
                    onChange={handleNameChange}
                    variant="outlined"
                    helperText="Use your company or department name — members will see this in their workspace switcher."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    variant="outlined"
                    multiline
                    rows={4}
                    helperText="Optional: a short note about what this organization is for."
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(-1)}
                      startIcon={<ArrowBack />}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      size="small"
                      color="primary"
                      disabled={!name.trim() || loading}
                      startIcon={loading ? <CircularProgress size={16} /> : <Save />}
                    >
                      {loading ? 'Creating...' : 'Create Organization'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Grid>

        {/* Right rail: getting started journey + orientation */}
        <Grid item xs={12} md={5}>
          {/* Journey */}
          <Box
            sx={(theme) => ({
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '14px',
              padding: 3,
              boxShadow: panelShadow(theme),
              marginBottom: 2,
            })}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AutoAwesomeOutlined
                sx={(theme) => ({ fontSize: 18, color: theme.palette.brand.teal })}
              />
              <Typography
                sx={(theme) => ({
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  color: theme.palette.text.primary,
                })}
              >
                Getting started
              </Typography>
            </Box>
            <Box sx={{ pl: 0.5 }}>
              {journeySteps.map((step, i) => (
                <JourneyRow
                  key={step.label}
                  step={step}
                  index={i}
                  isLast={i === journeySteps.length - 1}
                />
              ))}
            </Box>
          </Box>

          {/* Orientation card */}
          <Box
            sx={(theme) => ({
              backgroundColor: theme.palette.mode === 'light'
                ? 'rgba(34,181,220,0.06)'
                : 'rgba(34,181,220,0.10)',
              border: (theme.palette.mode === 'light'
                ? '1px solid rgba(34,181,220,0.20)'
                : '1px solid rgba(34,181,220,0.30)'),
              borderRadius: '14px',
              padding: 2.5,
            })}
          >
            <Typography
              sx={(theme) => ({
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '-0.01em',
                color: theme.palette.mode === 'light' ? theme.palette.brand.teal : theme.palette.brand.cyan,
                mb: 1,
              })}
            >
              What's an organization?
            </Typography>
            <Typography
              sx={(theme) => ({
                fontSize: 12.5,
                color: theme.palette.text.secondary,
                lineHeight: 1.55,
              })}
            >
              An organization is your top-level workspace in Tier One Content. Teams live inside it
              and each team owns its own content. Members get roles at either the org level
              (OrgAdmin / OrgMember) or the team level (TeamAdmin / TeamMember). You can move
              members between teams without leaving the organization.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default OrganizationCreate;
